<template>
    <div
        class="timetable-wrapper timetable-content"
        v-bind:class="'timetable-' + action"
    >
        <!--<div class="spinner-wrapper" v-if="isLoading">-->
        <!--<atom-spinner class="spinner"-->
        <!--:animation-duration="1000"-->
        <!--:size="60"-->
        <!--:color="'#ff1d5e'"/>-->
        <!--</div>-->

        <div class="timetable" v-show="!showingTrash" v-bind:class="{ 'show-dates': showHeaderDates }">
            <timetable-navigation
                :view="view"
                :date="date"
                :show-schedule-view="showScheduleView"
                :show-free-places-view="!!freeEventsUrl"
                :show-month-view="showMonthView"
                :show-trash-view="hasTrashView"
                :menu-open="menuOpen"
                :has-menu="hasMenu"
                :local-event-bus="eventBus"
                :is-loading="isLoading"
                :translations="texts"
            ></timetable-navigation>

            <!-- Context about what the grid is showing, between the navigation and the
                 grid itself. The consumer owns the content; the slot only fixes where it
                 sits, so it cannot push the navigation around.

                 Scoped, because the holiday controls live in this bar but their state
                 lives here: the consumer is our parent and cannot read it otherwise. -->
            <slot name="context"
                :vacation-select-mode="vacationSelectMode"
                :vacation-selected-count="selectedVacationDays.length"
                :show-vacation-action="!!vacationStoreUrl">

                <!-- Fallback: a page that embeds the calendar directly (the instructor's
                     own schedule) has no instructor bar to hang these on, and without it
                     the holiday controls simply vanished. It is already one instructor's
                     page, so the buttons are all it needs. -->
                <div class="tt-vacation-bar" v-if="vacationStoreUrl">
                    <reserve-menu
                        :select-mode="vacationSelectMode"
                        :selected-count="selectedVacationDays.length"
                        :texts="texts"
                        @reserve="openReservation"
                        @select-days="toggleVacationSelect()"
                        @confirm="confirmVacationSelection()"
                        @cancel="toggleVacationSelect()"
                    ></reserve-menu>
                </div>
            </slot>

            <div class="timetable-view">
                <timetable-view-week
                    v-if="view === 'week'"
                    :action="action"
                    :local-event-bus="eventBus"
                    :events="events"
                    :event-templates="eventTemplates"
                    :default-calendar-start-hour="calendarStartHour"
                    :default-calendar-end-hour="calendarEndHour"
                    :block-scheduling-after-hour="
                        blockSchedulingAfterHour || 24
                    "
                    :texts="texts"
                    :highlighted="highlighted"
                    :date="date"
                    :snap-minutes="snapMinutes"
                    :slot-snap-minutes="slotSnapMinutes"
                    :prevent-overlap="preventOverlap"
                    :default-duration-in-minutes="defaultDurationInMinutes"
                    :drag-mode="dragMode"
                    :restrict-practice-to-free-slots="restrictPracticeToFreeSlots"
                    :vacation-select-mode="vacationSelectMode"
                    :selected-vacation-days="selectedVacationDays"
                    :can-manage-vacations="!!vacationDeleteUrl"
                    :can-reserve="!!vacationStoreUrl"
                    :can-create="!!storeEventsUrl"
                    :hide-vacation-events="hideVacationEvents"
                ></timetable-view-week>

                <timetable-view-month
                    v-if="view === 'month'"
                    :action="action"
                    :local-event-bus="eventBus"
                    :events="events"
                    :event-templates="eventTemplates"
                    :texts="texts"
                    :highlighted="highlighted"
                    :date="date"
                ></timetable-view-month>

                <timetable-view-day
                    v-if="view === 'day'"
                    :action="action"
                    :local-event-bus="eventBus"
                    :events="events"
                    :event-templates="eventTemplates"
                    :default-calendar-start-hour="calendarStartHour"
                    :default-calendar-end-hour="calendarEndHour"
                    :block-scheduling-after-hour="
                        blockSchedulingAfterHour || 24
                    "
                    :texts="texts"
                    :highlighted="highlighted"
                    :date="date"
                    :snap-minutes="snapMinutes"
                    :slot-snap-minutes="slotSnapMinutes"
                    :prevent-overlap="preventOverlap"
                    :default-duration-in-minutes="defaultDurationInMinutes"
                    :drag-mode="dragMode"
                    :restrict-practice-to-free-slots="restrictPracticeToFreeSlots"
                    :can-manage-vacations="!!vacationDeleteUrl"
                    :can-reserve="!!vacationStoreUrl"
                    :can-create="!!storeEventsUrl"
                    :hide-vacation-events="hideVacationEvents"
                ></timetable-view-day>

                <timetable-view-schedule
                    v-if="view === 'schedule'"
                    :action="action"
                    :local-event-bus="eventBus"
                    :events="events"
                    :texts="texts"
                    :can-delete-events="true"
                    :date="date"
                ></timetable-view-schedule>

                <timetable-view-free-places
                    v-if="view === 'free_places'"
                    :action="action"
                    :local-event-bus="eventBus"
                    :hide-past-events="hidePastEvents"
                    :free-events-url="freeEventsUrl"
                    :texts="texts"
                    :date="date"
                    :selected-calendar="selectedCalendar"
                    :selected-car-model="selectedCarModel"
                    :selected-gearbox-type="selectedGearboxType"
                ></timetable-view-free-places>
            </div>
        </div>

        <!-- Standalone embeds (a student's practice tab) have no calendar list around
             them to swap in the trash, so when given a trash URL the calendar shows it
             itself, in place of the grid. -->
        <timetable-trash
            v-if="showingTrash"
            :calendars="null"
            :trash-url="trashUrl"
            :restore-url="restoreUrl"
            :trash-filters="trashFilters"
            :events-info-url="eventsInfoUrl"
            :translations="texts"
            @hide-trash-view="hideTrash"
        ></timetable-trash>

        <create-event-modal
            :action="action"
            :local-event-bus="eventBus"
            :default-event-title="texts.defaultEventTitle"
            :texts="texts"
            :event-extra-fields="eventExtraFields"
            :can-schedule-recurring-events="canScheduleRecurringEvents"
        ></create-event-modal>

        <edit-event-modal
            :action="action"
            :local-event-bus="eventBus"
            :event-extra-fields="eventExtraFields"
            :can-schedule-recurring-events="canScheduleRecurringEvents"
            :texts="texts"
        ></edit-event-modal>

        <show-event-modal
            :action="action"
            :events-info-url="eventsInfoUrl"
            :local-event-bus="eventBus"
            :can-edit-events="!!updateEventsUrl"
            :can-delete-events="!!deleteEventsUrl"
            :texts="texts"
        ></show-event-modal>

        <delete-event-modal
            :texts="texts"
            :local-event-bus="eventBus"
        ></delete-event-modal>

        <vacation-modal
            v-if="vacationStoreUrl"
            :vacation-store-url="vacationStoreUrl"
            :vacation-delete-url="vacationDeleteUrl"
            :local-event-bus="eventBus"
            :texts="texts"
        ></vacation-modal>
    </div>
</template>

<script>
import Vue from "vue";

import { AtomSpinner } from "epic-spinners";
// import { setupCalendar} from 'v-calendar'

import { scheduleEventMixin } from "./mixins/ScheduleEventMixin";
import { loading } from "./mixins/LoadingMixin";
import { EventBus } from "./mixins/EventBus";
import { EventAPI } from "./mixins/EventAPI";
import { ViewDates } from "./mixins/ViewDatesMixin";
import { ConfirmActionMixin } from "./mixins/ConfirmActionMixin";
import { sharedTimetableBus } from "./mixins/SharedTimetableBus";

import TimetableViewWeek from "./TimetableView-Week.vue";
import TimetableViewMonth from "./TimetableView-Month.vue";
import TimetableViewDay from "./TimetableView-Day.vue";
import TimetableViewSchedule from "./TimetableView-Schedule.vue";
import TimetableViewFreePlaces from "./TimetableView-FreePlaces.vue";
import TimetableNavigation from "./components/TimetableNavigation.vue";
// const TimeInput = require('./TimeInput');
import CreateEventModal from "./modals/CreateEventModal.vue";
import EditEventModal from "./modals/EditEventModal.vue";
import ShowEventModal from "./modals/ShowEventModal.vue";
import DeleteEventModal from "./modals/DeleteEventModal.vue";
import VacationModal from "./modals/VacationModal.vue";
import ReserveMenu from "./ReserveMenu.vue";
import { isBlockingEvent } from "./helpers/blockingEvents";
import TimetableList from "./TimetableList.vue";
import TimetableTrash from "./TimetableTrash.vue";

Vue.component("timetable-list", TimetableList);
Vue.component("timetable-trash", TimetableTrash);

export default {
    mixins: [
        scheduleEventMixin,
        loading,
        EventBus,
        EventAPI,
        ViewDates,
        ConfirmActionMixin,
    ],
    components: {
        AtomSpinner,
        // 'time-input': TimeInput,
        "timetable-view-week": TimetableViewWeek,
        "timetable-view-month": TimetableViewMonth,
        "timetable-view-day": TimetableViewDay,
        "timetable-view-schedule": TimetableViewSchedule,
        "timetable-view-free-places": TimetableViewFreePlaces,
        "timetable-navigation": TimetableNavigation,
        "create-event-modal": CreateEventModal,
        "edit-event-modal": EditEventModal,
        "show-event-modal": ShowEventModal,
        "delete-event-modal": DeleteEventModal,
        "vacation-modal": VacationModal,
        "reserve-menu": ReserveMenu,
    },
    props: {
        action: {
            // 'show' - Only show events and 'event templates'
            // 'edit' - Edit events (schedule events with and without 'event templates')
            // 'schedule' - Schedule events by available 'event templates'
            default: "show",

            validator: function (value) {
                // The value must match one of these strings
                return ["show", "edit", "schedule"].indexOf(value) !== -1;
            },
        },
        showDate: {
            default: () => new Date(),
        },
        showFirstEvent: {
            default: false,
        },
        showFirstWeek: {
            default: false,
        },
        showHeaderDates: {
            default: true,
        },
        hidePastEvents: {
            default: false,
        },
        hideVacationEvents: {
            default: false,
        },
        highlighted: {
            default: null,
        },
        eventExtraFields: {
            default: () => [],
        },
        defaultDurationInMinutes: {
            default: 120,
        },
        showEvents: {
            default: () => [],
        },
        showEventTemplates: {
            default: () => [],
        },
        eventsUrl: {
            default: null,
        },
        storeEventsUrl: {
            default: null,
        },
        deleteEventsUrl: {
            default: null,
        },
        updateEventsUrl: {
            default: null,
        },
        eventsInfoUrl: {
            default: null,
        },
        blockSchedulingAfterHour: {
            default: 24, // 24 - will not block scheduling
        },
        freeEventsUrl: {
            default: null,
        },
        scheduleEventParams: {
            default: () => {},
        },
        translations: {
            default: () => {},
        },
        viewType: {
            default: "week",
        },
        showScheduleView: {
            default: false,
        },
        showMonthView: {
            default: false,
        },
        showTrashView: {
            // Off by default: only the calendar list (TimetableList) listens for
            // 'show-trash-view', so on every standalone embed -- the instructor
            // dashboard, the admin action="edit" pages -- the menu item did nothing
            // when clicked. TimetableList still opts in by passing :show-trash-view;
            // a standalone embed opts in by passing a trash-url instead (see below).
            default: false,
        },
        trashUrl: {
            // When set, the calendar renders the trash itself (no calendar list needed).
            default: null,
        },
        restoreUrl: {
            default: null,
        },
        trashFilters: {
            default: () => ({}),
        },
        preventOverlap: {
            // Refuse to open the create dialog on top of an existing event; slide
            // the new one into the nearest gap instead. Used by the work schedule.
            default: false,
        },
        canScheduleRecurringEvents: {
            default: true,
        },
        defaultCalendarStartHour: {
            default: 7,
        },
        defaultCalendarEndHour: {
            default: 21,
        },
        selectedGearboxType: {
            default: null,
        },
        selectedCarModel: {
            default: null,
        },
        rescheduleEventsUrl: {
            default: null,
        },
        snapMinutes: {
            default: 15,
        },
        slotSnapMinutes: {
            // Step (minutes) a click or a sweep on the empty grid is snapped to.
            default: 30,
        },
        dragMode: {
            // 'manager' = magnetic (snap to free slot, else grid); 'student' = free-slots only.
            // 'off' = no drag.
            default: 'manager',
        },
        restrictPracticeToFreeSlots: {
            // When true, managers can drag PRACTICE lessons only onto free instructor slots.
            default: false,
        },
        menuOpen: {
            // Whether the left rail is visible. The navigation uses it to decide if it
            // has to offer its own free-places shortcut.
            default: true,
        },
        // Whether a rail exists to be toggled. Off by default: a calendar embedded on
        // its own (an instructor's page, a student's schedule) has no rail, and the
        // toggle for it rendered anyway with nothing listening.
        hasMenu: {
            default: false,
        },
        vacationStoreUrl: {
            // When set, the calendar exposes holiday (vacation) creation.
            default: null,
        },
        vacationDeleteUrl: {
            default: null,
        },
        syncGroup: {
            // Timetables sharing this key mirror each other's active date & view
            // (e.g. the two tabs on the instructor schedule page).
            default: null,
        },
    },

    data() {
        return {
            date: moment(this.showDate),
            view: this.viewType,
            showingTrash: false,
            events: this.showEvents,
            eventTemplates: this.showEventTemplates,
            calendarStartHour: this.defaultCalendarStartHour,
            calendarEndHour: this.defaultCalendarEndHour,
            texts: {
                save: "Salvează",
                cancel: "Renunță",
                delete: "Șterge",
                vacation_add: "Adaugă concediu",
                vacation_select_days: "Selectează zile",
                vacation_confirm: "Creează concediu",
                vacations: "Concedii",
                vacation_all_day: "Toată ziua",
                reserve: "Rezervă",
                reserve_type: "Tip rezervare",
                reservation_block: "Blochează",
                reservation_confirm: "Rezervă",
                slot_menu_event: "Eveniment",
                slot_menu_reservation: "Rezervare",
                select_calendar_to_create: "Selectează un instructor pentru a adăuga un eveniment",
                reloaded: "Calendarul a fost reîncărcat",
                more_options: "Mai multe opțiuni",
                repeat_event: "Repetă eveniment",
                add_event_title: "Adaugă titlu",
                add_event_note: "Notă (opțional)",
                days_of_week: "Zile din săptămână",
                no_title: "Liber",
                exam: "Examen",
                today: "Astăzi",
                price: "Preț",
                start_date: "Data",
                end_date: "Dată sfârșit repetare",
                start_hour: "De la ora",
                end_hour: "Până la ora",
                delete_recurring_event: "Șterge evenimentul repetitiv",
                delete_event: "Șterge evenimentul",
                delete_event_description:
                    "Sigur doriți să anulați această lecție?",
                delete_recurring_event_description:
                    "Alegeți ce parte din seria de evenimente doriți să o anulați.",
                delete_this_event: "Acest eveniment",
                delete_this_and_following_events:
                    "Acesta și evenimentele viitoare",
                delete_all_events: "Toate evenimentele",
                delete_anyway: "Anulează lecția oricum",
                required_lesson_warning_title:
                    "Avertizare pentru lecție obligatorie",
                required_lesson_warning_description:
                    "Anularea acestei lecții poate afecta admiterea la examen. Dacă continuați, trebuie să replanificați numărul necesar de lecții practice până la examen.",
                recurring_end: "Sfârșit repetare",
                recurring_never_ends: "Niciodată",
                recurring_ends_on_date: "Pe data de",
                defaultEventTitle: "",
                ...this.translations,
            },
            selectedEvent: null,
            selectedEventTemplate: null,
            selectedDate: null,
            selectedCalendar: null,
            vacationSelectMode: false,
            selectedVacationDays: [],
        };
    },

    created() {
        this.fixEventsData();
        this.getEvents().then(() => {
            this.fixInitialDate();
            this.fixEventsData();
        });

        // setupCalendar({
        //   locale: window.userLocale || "ro",
        //   firstDayOfWeek: 2,
        // });
    },

    mounted() {
        console.log("Timetable Component Mounted.");

        this.eventBus.$on("show-trash-view", this.showTrash);
        this.eventBus.$on("add-new-event", this.addNewEvent);
        this.eventBus.$on("add-new-reservation", this.addNewReservation);
        this.eventBus.$on("create-event-unavailable", this.createEventUnavailable);
        this.eventBus.$on("created-event", this.createdEvent);
        this.eventBus.$on("click-event", this.clickEvent);
        this.eventBus.$on("click-event-template", this.clickEventTemplate);
        this.eventBus.$on(
            "click-free-event-template",
            this.clickFreeEventTemplate
        );
        this.eventBus.$on("updated-event", this.updatedEvent);
        this.eventBus.$on("deleted-event", this.deletedEvent);
        this.eventBus.$on("view-changed", this.viewChanged);
        this.eventBus.$on("date-changed", this.dateChanged);
        this.eventBus.$on("selected-calendar", this.setSelectedCalendar);
        this.eventBus.$on("update-events", () => this.getEvents());
        this.eventBus.$on("update-events-force", () => this.getEvents());
        this.eventBus.$on("reload-calendar", this.reloadCalendar);
        this.eventBus.$on("toggle-vacation-select", this.toggleVacationSelect);
        this.eventBus.$on("open-reservation", this.openReservation);
        this.eventBus.$on("toggle-vacation-day", this.toggleVacationDay);
        this.eventBus.$on("confirm-vacation-selection", this.confirmVacationSelection);
        this.eventBus.$on("vacation-saved", this.clearVacationSelection);
        this.eventBus.$on("manage-vacation", this.manageVacation);

        if (this.syncGroup) {
            sharedTimetableBus.$on("date-view-changed", this.onSharedDateView);
        }

        // if (this.view === "week" && window.innerWidth < 768) {
        //     this.eventBus.$emit("view-changed", "day");
        // }
    },

    beforeDestroy() {
        if (this.syncGroup) {
            sharedTimetableBus.$off("date-view-changed", this.onSharedDateView);
        }
    },

    methods: {
        showTrash() {
            // Inside a calendar list the list owns the trash view; only act when
            // this calendar was given its own trash URL.
            if (!this.trashUrl) return;

            this.showingTrash = true;
        },

        hideTrash() {
            this.showingTrash = false;
            this.getEvents();
        },

        addNewEvent(date, start_time, end_time) {
            if (this.action === "edit") {
                end_time =
                    end_time ||
                    moment(start_time).add(
                        this.defaultDurationInMinutes,
                        "minutes"
                    );

                this.eventBus.$emit("create-event", {
                    start_date: date,
                    start_time: start_time.format("HH:mm"),
                    end_time: end_time.format("HH:mm"),
                });
            }
        },

        // The same slot, but blocked instead of booked: the reservation modal opens
        // preset to that day and hour range, so all that is left to fill is why.
        addNewReservation(date, start_time, end_time) {
            if (this.action !== "edit" || !this.vacationStoreUrl) return;

            // A sweep to the bottom of the grid can end at midnight, which the
            // time field cannot hold.
            const end = end_time.isSame(moment(end_time).startOf("day"))
                ? moment(start_time).endOf("day")
                : end_time;

            this.eventBus.$emit("open-vacation-modal", {
                type: "reservation",
                dates: [moment(date).format("YYYY-MM-DD")],
                full_day: false,
                start_time: start_time.format("HH:mm"),
                end_time: end.format("HH:mm"),
            });
        },

        // A click on the grid with nowhere to store an event (no instructor picked
        // yet). Say so, and put the instructor list in front of the user when the
        // rail is closed, as the hint under the navigation does.
        createEventUnavailable() {
            this.$notify({
                group: "app",
                type: "warning",
                text: this.texts.select_calendar_to_create,
            });

            if (this.hasMenu && !this.menuOpen) {
                this.eventBus.$emit("toggle-menu");
            }
        },

        createdEvent(event) {
            // this.date = moment(event.start_time);
            this.fixEventData(event);
            this.events.push(event);
            // this.getEvents();
        },

        clickEvent(event, date) {
            if (event.disabled) return;

            this.selectedEvent = event;
            this.selectedDate = date;

            this.eventBus.$emit("show-event", event, date);
        },

        clickEventTemplate(eventTemplate, date) {
            this.selectedEventTemplate = eventTemplate;
            this.selectedDate = date;

            switch (this.action) {
                case "edit":
                    this.eventBus.$emit("create-event", {
                        title: eventTemplate.title,
                        type: eventTemplate.type,
                        start_date: date,
                        start_time: eventTemplate._start_time.format("HH:mm"),
                        end_time: eventTemplate._end_time.format("HH:mm"),
                    });
                    break;

                case "schedule":
                    console.log(123, eventTemplate, date, this.scheduleEventParams);
                    this.scheduleEventByTemplate(
                        eventTemplate,
                        date,
                        this.scheduleEventParams
                    );
                    break;
            }
        },

        clickFreeEventTemplate(eventTemplate, date) {
            this.selectedEventTemplate = eventTemplate;
            this.selectedDate = date;

            switch (this.action) {
                case "schedule":
                    this.scheduleEventByTemplate(
                        eventTemplate,
                        date,
                        this.scheduleEventParams
                    );
                    break;
            }
        },

        updatedEvent(event) {
            this.fixEventData(event);
            Vue.set(
                this.events,
                this.events.findIndex((e) => e.id === event.id),
                event
            );
        },

        deletedEvent(event, date) {
            const deletedEventIndex = this.events.findIndex(
                (existingEvent) => existingEvent.id === event.id
            );

            if (deletedEventIndex >= 0) {
                this.$delete(this.events, deletedEventIndex);
            }

            // console.log(123, event._parent_event_template);
            if (event._parent_event_template) {
                delete event._parent_event_template._created_event;
            }

            this.$emit("deleted-event");
            this.getEvents();
        },

        dateChanged(date) {
            this.date = moment(date);
            this.publishSharedDateView();
        },

        viewChanged(view) {
            this.view = view;
            this.publishSharedDateView();
        },

        publishSharedDateView() {
            if (!this.syncGroup) return;

            sharedTimetableBus.$emit("date-view-changed", {
                group: this.syncGroup,
                source: this._uid,
                date: this.date.toDate(),
                view: this.view,
            });
        },

        onSharedDateView(payload) {
            // Ignore our own broadcasts and other groups.
            if (payload.group !== this.syncGroup || payload.source === this._uid) return;

            // Set directly (not via the *-changed handlers) so we don't echo back.
            if (!this.date.isSame(payload.date, "day")) {
                this.date = moment(payload.date);
            }
            if (this.view !== payload.view) {
                this.view = payload.view;
            }
        },

        fixInitialDate() {
            if (
                ((this.showFirstEvent && this.date.isSame(moment(), "day")) ||
                    this.showFirstWeek) &&
                this.events.length
            ) {
                let first_date = moment(this.events[0]._start_time);
                let last_date = moment(this.events[0]._start_time);
                this.events.forEach((event) => {
                    if (event._start_time < first_date) {
                        first_date = event._start_time;
                    }
                    if (event._start_time > last_date) {
                        last_date = event._start_time;
                    }
                });

                if (
                    first_date > this.date ||
                    last_date < this.date ||
                    this.showFirstWeek
                ) {
                    this.date = first_date;
                    this.eventBus.$emit("date-changed", this.date);
                }
            }
        },

        setSelectedCalendar(calendar) {
            this.selectedCalendar = calendar;
        },

        toggleVacationSelect() {
            this.vacationSelectMode = !this.vacationSelectMode;
            this.selectedVacationDays = [];
        },

        // "Rezervă ▾" menu: the type is chosen before the modal opens, but the
        // modal still shows it so it can be switched.
        openReservation(type = "vacation") {
            this.eventBus.$emit("open-vacation-modal", { type });
        },

        toggleVacationDay(date) {
            const key = moment(date).format("YYYY-MM-DD");
            const index = this.selectedVacationDays.indexOf(key);

            if (index >= 0) {
                this.selectedVacationDays.splice(index, 1);
            } else {
                this.selectedVacationDays.push(key);
            }
        },

        confirmVacationSelection() {
            if (!this.selectedVacationDays.length) return;

            // Pass the exact selected days so a non-consecutive selection creates a
            // holiday only on those days (not a filled range from the first to the last).
            this.eventBus.$emit("open-vacation-modal", {
                dates: [...this.selectedVacationDays].sort(),
                full_day: true,
            });

            // The selection is kept until the modal reports a successful save
            // ("vacation-saved"). Dropping it here meant closing or cancelling the
            // modal silently discarded every picked day.
        },

        clearVacationSelection() {
            this.vacationSelectMode = false;
            this.selectedVacationDays = [];
        },

        // The gear menu's "Reîncarcă" emitted into the void here: only the free-places
        // view ever listened for it, so in week/day/month the button did nothing at all.
        // Re-fetching on its own is still almost invisible -- the spinner is gone before
        // the eye catches it -- so confirm the reload landed the way every other write
        // in this component does.
        reloadCalendar() {
            return this.getEvents()
                .then(() => {
                    this.$notify({
                        group: "app",
                        text: this.texts.reloaded,
                        type: "success",
                    });
                })
                .catch(() => {
                    // EventAPI clears the flag in its .then, which a rejection skips --
                    // leaving the spinner turning forever.
                    this.loading(false);
                    this.$notify({
                        group: "app",
                        text: "A apărut o eroare. Încercați din nou.",
                        type: "error",
                    });
                });
        },

        manageVacation(event) {
            // Derive the whole holiday (all days sharing the group) from the loaded
            // events so the manage modal can delete or re-create exactly those days,
            // even when the holiday spans non-consecutive days.
            const groupId = event.event_group_id;
            const dates = this.events
                .filter((e) => isBlockingEvent(e) && e.event_group_id === groupId)
                .map((e) => moment(e._start_time).format("YYYY-MM-DD"))
                .sort();
            const fullDay = !!event.is_full_day_event;

            this.eventBus.$emit("open-vacation-modal", {
                editGroupId: groupId,
                dates,
                type: event.type,
                full_day: fullDay,
                start_time: fullDay ? "09:00" : moment(event._start_time).format("HH:mm"),
                end_time: fullDay ? "13:00" : moment(event._end_time).format("HH:mm"),
                reason: event.title || "",
                reason_code: (event.extra_options && event.extra_options.reason_code) || null,
            });
        },
    },
    computed: {
        hasTrashView() {
            return this.showTrashView || !!this.trashUrl;
        },
    },

    filters: {},

    watch: {
        showDate(showDate) {
            this.date = moment(showDate);
        },

        eventsUrl() {
            this.events = [];
            this.eventTemplates = [];

            if (this.view !== "free_places") {
                this.getEvents();
            }
        },

        viewStartDate() {
            this.getEventsBetweenDates();
        },
    },
};
</script>
<style lang="scss">
@use "./css/timetable-view.scss" as *;
@use "./css/timetable-custom.scss" as *;
</style>
